import { Button, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useFirebaseAuth } from '../../lib/firebase';
import { isUserCancellation, registerPasskey } from '../../lib/passkeys';
import { rememberPasskeyPromptDismissed } from '../../lib/passkeyPrompt';
import { getLogger } from '../../lib/logger';
import { SheetsData } from '../Sheets';

const logger = getLogger('passkeys');

/**
 * Offers passkey enrolment once, straight after a successful sign-in.
 *
 * That moment is deliberate: the user has just done the slow thing, so "skip that next time" is
 * concrete rather than hypothetical, and we are holding a fresh ID token -- which registration needs
 * in order to bind the credential to this uid rather than minting a parallel account.
 *
 * Three outcomes, because deferring and refusing are genuinely different answers: enrol, ask again
 * next time, or stop asking. Only the last writes the per-device flag. An earlier version offered a
 * single "Not now" that recorded a permanent dismissal, which meant anyone who read it as a
 * deferral was never offered again.
 *
 * The flag is recorded only on a real decision, never merely because the sheet rendered, so an offer
 * the user never actually saw comes back instead of being burned.
 */
function PasskeyPromptForm({ uid, onClose }: { uid: string | undefined, onClose: () => void }) {
    const auth = useFirebaseAuth();
    const [ busy, setBusy ] = useState(false);

    /** "Don't ask again" -- and the landing spot for a failure that would otherwise keep repeating. */
    const dismissPermanently = () => {
        rememberPasskeyPromptDismissed(uid);
        onClose();
    };

    /** "Remind me next time" -- deliberately records nothing, so the offer returns on next sign-in. */
    const deferToNextTime = () => {
        onClose();
    };

    const onSetUp = async () => {
        setBusy(true);
        try {
            await registerPasskey(auth);
            rememberPasskeyPromptDismissed(uid);
            notifications.show({
                title: 'Passkey added',
                message: 'Next time, sign in with Face ID or your passcode.',
            });
            onClose();
        } catch (err) {
            if (isUserCancellation(err)) {
                // Backing out of the system sheet isn't a decision about the offer itself, so leave
                // the prompt un-dismissed and let them try again.
                logger.log('User cancelled passkey enrolment');
            } else {
                logger.warn(`Passkey enrolment failed: ${err}`);
                notifications.show({
                    color: 'red',
                    title: 'Could not set up a passkey',
                    message: err instanceof Error ? err.message : 'You can try again in Settings.',
                });
                // A real failure shouldn't strand the user in a sheet that keeps failing.
                dismissPermanently();
            }
        } finally {
            setBusy(false);
        }
    };

    return <Stack p='md' gap='md'>
        <Text>
            Skip the email next time — sign in with Face ID, Touch ID or your device passcode.
        </Text>
        <Text size='sm' c='dimmed'>
            The passkey is stored on this device, and signing in by email link keeps working -- so you
            can still get back in on a new device. Manage passkeys any time in Settings.
        </Text>
        <Button onClick={onSetUp} loading={busy}>Set up a passkey</Button>
        <Button variant='subtle' onClick={deferToNextTime} disabled={busy}>Remind me next time</Button>
        {/* Dimmed rather than merely subtle: it is the one irreversible choice here, in the sense
            that nothing will bring the offer back except Settings. */}
        <Button variant='subtle' color='gray' onClick={dismissPermanently} disabled={busy}>
            Don&apos;t ask again
        </Button>
    </Stack>;
}

export function showPasskeyPrompt(sheets: SheetsData, options: { uid: string | undefined }) {
    sheets.showSheetWith(
        context => <PasskeyPromptForm
            uid={options.uid}
            onClose={() => context.removeSheet()}
        />,
        { title: 'Sign in faster next time?' }
    );
}
